import { api } from "@convex/_generated/api"; // Use alias
import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { convex } from "@/lib/convexClient";

interface IdeenModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export function IdeenModal({ isOpen, onClose }: IdeenModalProps) {
	const [betreff, setBetreff] = useState("");
	const [beschreibung, setBeschreibung] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setSuccess(false);
		setIsSubmitting(true);

		try {
			await convex.mutation(api.ideen.ideeSpeichern, { betreff, beschreibung });
			setSuccess(true);
			setTimeout(() => {
				setBetreff("");
				setBeschreibung("");
				setSuccess(false);
				onClose();
			}, 1500);
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: "Ein unbekannter Fehler ist aufgetreten",
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog onOpenChange={(open) => !open && onClose()} open={isOpen}>
			<DialogContent className="backdrop-blur-sm sm:max-w-lg dark:bg-gray-800/95">
				<DialogHeader>
					<DialogTitle className="text-gray-800 text-xl dark:text-gray-100">
						Idee einreichen
					</DialogTitle>
					<DialogDescription className="text-gray-600 dark:text-gray-300">
						Teile deine Idee für neue Tools oder Verbesserungen mit uns.
					</DialogDescription>
				</DialogHeader>

				<form className="space-y-4 py-2" onSubmit={handleSubmit}>
					<div className="space-y-2">
						<Label
							className="text-gray-700 dark:text-gray-300"
							htmlFor="betreff"
						>
							Betreff
						</Label>
						<Input
							className="border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
							id="betreff"
							onChange={(e) => setBetreff(e.target.value)}
							placeholder="Wie heißt deine Idee?"
							required
							value={betreff}
						/>
					</div>

					<div className="space-y-2">
						<Label
							className="text-gray-700 dark:text-gray-300"
							htmlFor="beschreibung"
						>
							Beschreibung
						</Label>
						<Textarea
							className="min-h-[120px] border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
							id="beschreibung"
							onChange={(e) => setBeschreibung(e.target.value)}
							placeholder="Beschreibe deine Idee..."
							required
							value={beschreibung}
						/>
					</div>

					{error && (
						<div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-700 text-sm dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
							{error}
						</div>
					)}

					{success && (
						<div className="flex items-center rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-700 text-sm dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
							<Check className="mr-2 h-4 w-4" />
							Deine Idee wurde erfolgreich gespeichert!
						</div>
					)}
				</form>

				<DialogFooter className="mt-2">
					<Button
						className="border-gray-200 dark:border-gray-700"
						disabled={isSubmitting}
						onClick={onClose}
						type="button"
						variant="outline"
					>
						Abbrechen
					</Button>
					<Button
						className="bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
						disabled={isSubmitting || !betreff || !beschreibung}
						onClick={handleSubmit}
						type="button"
					>
						{isSubmitting ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Speichern...
							</>
						) : (
							"Idee speichern"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
